import React from "react";

import { useTranslation } from "react-i18next";
import ViewDialog from "@components/Dialog";
import InformationView from "@components/InformationView";
import { Divider, Box, CircularProgress } from "@mui/material";
import CustomTabs, {
  TabContent,
  TabGroup,
  TabName,
} from "@components/CustomTab";
import { parseValue } from "@lib/utils/string";
import { Unit } from "@lib/types/units";
import { useQuery } from "@tanstack/react-query";
import { DriversList } from "@lib/types/driver";
import { getUnitDetails, getUnits } from "@lib/api/queries/units";
import DriverCard from "@components/DriverCard";

interface Props {
  viewUnitDialogOpen: boolean;
  setViewUnitDialogOpen: (open: boolean) => void;
  unitData?: Unit;
}

const ViewUnit: React.FC<Props> = ({
  viewUnitDialogOpen,
  setViewUnitDialogOpen,
  unitData,
}) => {
  const { t } = useTranslation();

  const userInfo = [
    {
      label: t("user.units.unitName"),
      value: parseValue(unitData?.name),
      columnSize: 2,
    },
    {
      label: t("user.units.iccid"),
      value: parseValue(unitData?.iccid),
      columnSize: 2,
    },
  ];

  const { data: binded_drivers, isFetching: isBindedDriversLoading } =
    useQuery<DriversList>({
      queryKey: ["unit-drivers", unitData?.unit_id],
      queryFn: () => getUnitDetails(unitData?.unit_id as number),
      enabled: !!unitData?.unit_id,
    });

  return (
    <ViewDialog
      title={t("user.units.view")}
      isOpen={viewUnitDialogOpen}
      handleClose={() => {
        setViewUnitDialogOpen(false);
      }}
    >
      <InformationView title="Unit Information" data={userInfo} columns={4} />

      <Box sx={{ borderTop: 1, borderColor: "divider" }}>
        <CustomTabs>
          <TabGroup>
            <TabName>{t("user.units.bindedDrivers")}</TabName>
            <TabContent>
              {isBindedDriversLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "300px",
                    padding: 2,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  flex={1}
                  flexDirection={"column"}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    padding: 2,
                  }}
                >
                  {binded_drivers?.data?.length === 0 ? (
                    <p>{t("user.units.noBindedDrivers")}</p>
                  ) : (
                    binded_drivers?.data?.map((driver) => (
                      <DriverCard driver={driver} />
                    ))
                  )}
                </Box>
              )}
            </TabContent>
          </TabGroup>
        </CustomTabs>
      </Box>
    </ViewDialog>
  );
};

export default ViewUnit;
